import { useState } from 'react';
import { X, Plus, Calendar, Tag } from 'lucide-react';
import type { TodoTag, CreateTodoInput } from '../../types/todo';
import { TODO_TAG_LABELS, TODO_TAG_COLORS } from '../../types/todo';
import { logger } from '../../lib/logger';

interface ToDoCreateFormProps {
  onSubmit: (input: CreateTodoInput) => Promise<void>;
  onCancel: () => void;
}

const tagOptions: TodoTag[] = ['lead', 'form', 'website', 'general'];

export function ToDoCreateForm({ onSubmit, onCancel }: ToDoCreateFormProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [tag, setTag] = useState<TodoTag | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !dueDate) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim() || undefined,
        due_date: dueDate,
        tag: tag || undefined,
      });
      setTitle('');
      setDescription('');
      setTag('');
    } catch (error) {
      logger.error('Error creating todo:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-[#1A1A1A] dark:text-white">Neue Aufgabe</h3>
        <button
          onClick={onCancel}
          className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
        >
          <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-[#1A1A1A] dark:text-white mb-2">
            Titel *
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Was muss erledigt werden?"
            className="w-full px-4 py-3 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white placeholder-gray-400 dark:placeholder-slate-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            required
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-[#1A1A1A] dark:text-white mb-2">
            Beschreibung
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Weitere Details..."
            rows={3}
            className="w-full px-4 py-3 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white placeholder-gray-400 dark:placeholder-slate-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="dueDate" className="block text-sm font-medium text-[#1A1A1A] dark:text-white mb-2">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#6B6B6B] dark:text-slate-400" />
                Fälligkeitsdatum *
              </div>
            </label>
            <input
              id="dueDate"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1A1A1A] dark:text-white mb-2">
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-[#6B6B6B] dark:text-slate-400" />
                Tag (optional)
              </div>
            </label>
            <div className="flex flex-wrap gap-2">
              {tagOptions.map((tagOption) => {
                const colors = TODO_TAG_COLORS[tagOption];
                const isSelected = tag === tagOption;
                return (
                  <button
                    key={tagOption}
                    type="button"
                    onClick={() => setTag(isSelected ? '' : tagOption)}
                    className={`
                      px-3 py-2 rounded-xl text-sm font-medium transition-all
                      ${
                        isSelected
                          ? `${colors.bg} ${colors.text} ring-2 ring-offset-1 ring-primary`
                          : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600'
                      }
                    `}
                  >
                    {TODO_TAG_LABELS[tagOption]}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-3 border border-gray-200 dark:border-slate-600 text-[#6B6B6B] dark:text-slate-400 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors font-medium"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={!title.trim() || !dueDate || isSubmitting}
            className="flex-1 px-4 py-3 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {isSubmitting ? 'Erstelle...' : 'Erstellen'}
          </button>
        </div>
      </form>
    </div>
  );
}
